import { getData, postData, postDataImg } from "@/request/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

export const useGetContactInfo = () => {
  return useQuery({
    queryKey: ["contact-info"],
    queryFn: async () => {
      const response = await getData("contact-info");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetPageSettings = () => {
  return useQuery({
    queryKey: ["page-settings"],
    queryFn: async () => {
      const response = await getData("page-settings");
      return response.data?.data as {
        capabilities: boolean;
        clients: boolean;
        careers: boolean;
      };
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetSiteContent = () => {
  return useQuery({
    queryKey: ["site-content"],
    queryFn: async () => {
      const response = await getData("site-content");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetAbout = () => {
  return useQuery({
    queryKey: ["about"],
    queryFn: async () => {
      const response = await getData("about");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetWhoWeAre = () => {
  return useQuery({
    queryKey: ["who-we-are"],
    queryFn: async () => {
      const response = await getData("who-we-are");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetWhyChoose = () => {
  return useQuery({
    queryKey: ["why-choose"],
    queryFn: async () => {
      const response = await getData("why-choose");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetExcellence = () => {
  return useQuery({
    queryKey: ["excellence"],
    queryFn: async () => {
      const response = await getData("excellence");
      return response.data?.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetBanners = () => {
  return useQuery({
    queryKey: ["banners"],
    queryFn: async () => {
      const response = await getData("banner");
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await getData("categories");
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 12 * 60 * 60 * 1000, // Data is fresh for 12 hours
  });
};

export const useGetCapabilities = () => {
  return useQuery({
    queryKey: ["capabilities"],
    queryFn: async () => {
      const response = await getData("capabilities");
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 12 * 60 * 60 * 1000, // Data is fresh for 12 hours
  });
};

export const useGetProjects = ({ page = 1, limit = 10 }) => {
  return useQuery({
    queryKey: ["projects", page, limit],
    queryFn: async () => {
      const response = await getData(`projects?page=${page}&limit=${limit}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetCertifications = ({ page = 1, limit = 10 }) => {
  return useQuery({
    queryKey: ["certifications", page, limit],
    queryFn: async () => {
      const response = await getData(
        `certifications?page=${page}&limit=${limit}`,
      );
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetClients = ({ page = 1, limit = 10 }) => {
  return useQuery({
    queryKey: ["clients", page, limit],
    queryFn: async () => {
      const response = await getData(`clients?page=${page}&limit=${limit}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetCareers = ({ page = 1, limit = 10 }) => {
  return useQuery({
    queryKey: ["careers", page, limit],
    queryFn: async () => {
      const response = await getData(`careers?page=${page}&limit=${limit}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetCareer = (slug: string) => {
  return useQuery({
    queryKey: ["career", slug],
    queryFn: async () => {
      const response = await getData(`careers/${slug}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetProducts = ({
  page = 1,
  limit = 10,
  isFeatured = null,
  category = "all",
  subCategory = "all",
}: any) => {
  return useQuery({
    queryKey: ["products", page, limit, isFeatured, category, subCategory],
    queryFn: async () => {
      const response = await getData(
        `products?page=${page}&limit=${limit}&isFeatured=${isFeatured}&category=${category}&subCategory=${subCategory}`,
      );
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetCoatings = ({
  page = 1,
  limit = 24,
  finish = "all",
}: { page?: number; limit?: number; finish?: string }) => {
  return useQuery({
    queryKey: ["coatings", page, limit, finish],
    queryFn: async () => {
      const response = await getData(
        `coatings?page=${page}&limit=${limit}&finish=${encodeURIComponent(finish)}`,
      );
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetCoating = (slug: string) => {
  return useQuery({
    queryKey: ["coating", slug],
    queryFn: async () => {
      const response = await getData(`coatings/${slug}`);
      return response.data?.coating;
    },
    enabled: !!slug,
    retry: 1,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetProduct = (slug: string) => {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const response = await getData(`products/${slug}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useApplyApplications = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await postDataImg(`careers/apply`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Application submitted successfully.");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to submit application.",
      );
    },
  });
};

export const useGetServices = ({ page = 1, limit = 3 }) => {
  return useQuery({
    queryKey: ["services", page, limit],
    queryFn: async () => {
      const response = await getData(`services?page=${page}&limit=${limit}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000, // Garbage collect after 24 hours
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  });
};

export const useGetService = (slug: string) => {
  return useQuery({
    queryKey: ["service", slug],
    queryFn: async () => {
      const response = await getData(`services/${slug}`);
      return response.data;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSendContactMessage = () => {
  return useMutation({
    mutationFn: async (data: {
      name: string;
      email: string;
      phone?: string;
      message: string;
    }) => {
      const response = await postData("contact-messages", data);
      return response.data;
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "حدث خطأ أثناء إرسال الرسالة، حاول مرة أخرى.",
      );
    },
  });
};

export const useGetSiteLogo = () => {
  return useQuery({
    queryKey: ["site-logo"],
    queryFn: async () => {
      const response = await getData("admin/logo");
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
  });
};
